import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import { addBadgeSubjectSchema } from 'schemas/badges'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Radio,
  Typography
} from '@material-ui/core'
import { Box, Chip, Divider, Stack } from '@mui/material'
import {
  Close as CloseIcon,
  CropLandscape,
  CropPortrait,
  Download,
  Print,
  Warning as WarningIcon
} from '@material-ui/icons'
import CustomTextField from 'components/Form/CustomTextField'
import ProgressButton from 'components/ProgressButton'
import { badgesSubjectsApplication } from 'constants/index'
import badgesSlice, {
  addBadgeSubject,
  applicableBadgeConfiguration,
  badgeCounterExists,
  clearRecentBadgeLabels,
  getBadgeSubject,
  getHighestBadgeCounter,
  updateBadgeSubject,
  updateRecentBadgeLabels
} from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { useStyles } from 'views/admin/BadgesV1/AddEditBadgeSubject/styles'
import BadgePreview from 'views/admin/BadgesV1/Configurations/BadgePreview'

const PAGE_SIZE_OPTIONS = [
  { id: 'letter', label: 'US Letter' },
  { id: 'a4', label: 'A4' }
]

const AddEditBadgeSubjectModal = ({
  open,
  onClose,
  subjectId,
  onSaveAndPrint,
  onSaveAndDownload
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isEditing = Boolean(subjectId)

  const formikRef = useRef(null)
  const submitIntentRef = useRef('save')

  const [saving, setSaving] = useState(false)
  const [loadingCounter, setLoadingCounter] = useState(false)
  const [subjectReady, setSubjectReady] = useState(false)
  const [lastCounter, setLastCounter] = useState(0)
  const [isBadgeCounterExists, setIsBadgeCounterExists] = useState(false)
  const [isCheckingCounter, setIsCheckingCounter] = useState(false)
  const [newBadgeCounter, setNewBadgeCounter] = useState(0)
  const [selectedType, setSelectedType] = useState('')

  // Preview / print config state
  const [pageSize, setPageSize] = useState(() => localStorage.getItem('badgePageSize') || 'letter')
  const [orientation, setOrientation] = useState(
    () => localStorage.getItem('badgeOrientation') || 'landscape'
  )
  const [bannerLabel, setBannerLabel] = useState('')
  const [recentLabelsByType, setRecentLabelsByType] = useState({})
  const [badgeConfig, setBadgeConfig] = useState(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [clearingLabels, setClearingLabels] = useState(false)

  const { conference } = useSelector((state) => state.conference)
  const { badgeSubject } = useSelector((state) => state.badges)

  const activeSubjectType = isEditing ? badgeSubject?.type : selectedType
  const recentLabels = recentLabelsByType[activeSubjectType] || []

  // Load existing subject when editing
  useEffect(() => {
    if (subjectId && conference?.id) {
      setSubjectReady(false)
      dispatch(getBadgeSubject(subjectId)).then(() => setSubjectReady(true))
    } else if (!subjectId) {
      setSubjectReady(true)
    }
    return () => {
      dispatch(badgesSlice.actions.setBadgeSubject(null))
      setSubjectReady(false)
    }
  }, [subjectId, conference?.id])

  // Sync selectedType from loaded subject (edit mode) or reset on open (add mode)
  useEffect(() => {
    if (open) {
      if (isEditing && badgeSubject?.type) {
        setSelectedType(badgeSubject.type)
      } else if (!isEditing) {
        setSelectedType('')
        setLastCounter(0)
        setIsBadgeCounterExists(false)
      }
    }
  }, [open, isEditing, badgeSubject?.type])

  // Load badge config + recent labels when type or modal open changes
  useEffect(() => {
    if (!open) return
    setBadgeConfig(null)
    setRecentLabelsByType({})
    setBannerLabel('')
    const type = isEditing ? badgeSubject?.type : selectedType
    if (conference?.id && type) {
      setLoadingConfig(true)
      applicableBadgeConfiguration(type, conference.id)
        .then((config) => {
          setBadgeConfig(config)
          const labelsByType =
            config?.recentLabels && !Array.isArray(config.recentLabels) ? config.recentLabels : {}
          setRecentLabelsByType(labelsByType)
          const recents = labelsByType[type] || []
          setBannerLabel(recents[0] || type)
          const savedOrientation = localStorage.getItem('badgeOrientation')
          if (!savedOrientation && config?.badgeOrientation) {
            setOrientation(config.badgeOrientation)
          }
        })
        .finally(() => setLoadingConfig(false))
    }
  }, [open, selectedType, isEditing ? badgeSubject?.type : null, conference?.id])

  // Load highest counter on type change — skip in edit mode (counter comes from the loaded subject)
  useEffect(() => {
    const loadHighestCounter = async () => {
      if (!conference?.id || !selectedType || isEditing) return
      setLoadingCounter(true)
      const lastBadge = await getHighestBadgeCounter(conference.id, selectedType)
      const next = lastBadge ? lastBadge.counter + 1 : 1
      setLastCounter(lastBadge ? lastBadge.counter : 0)
      if (formikRef.current) {
        formikRef.current.setFieldValue('counter', next)
        formikRef.current.setFieldValue('type', selectedType)
      }
      setLoadingCounter(false)
    }
    loadHighestCounter()
  }, [conference?.id, selectedType])

  const initialValues = {
    counter: isEditing ? badgeSubject?.counter ?? '' : lastCounter + 1,
    type: isEditing ? badgeSubject?.type ?? '' : selectedType,
    firstName: badgeSubject?.firstName ?? '',
    lastName: badgeSubject?.lastName ?? '',
    credential: badgeSubject?.credential ?? '',
    position: badgeSubject?.position ?? '',
    organization: badgeSubject?.organization ?? '',
    phone: badgeSubject?.phone ?? '',
    email: badgeSubject?.email ?? '',
    city: badgeSubject?.city ?? '',
    state: badgeSubject?.state ?? '',
    country: badgeSubject?.country ?? ''
  }

  const debouncedCheckCounter = useMemo(
    () =>
      debounce(async (value, originalCounter) => {
        if (parseInt(value) === originalCounter) {
          setIsBadgeCounterExists(false)
          return
        }
        setIsCheckingCounter(true)
        const exists = await badgeCounterExists(value, conference?.id, selectedType || undefined)
        setIsBadgeCounterExists(exists)
        setIsCheckingCounter(false)
      }, 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conference?.id, selectedType]
  )

  const handleOrientationChange = (val) => {
    setOrientation(val)
    localStorage.setItem('badgeOrientation', val)
  }

  const handlePageSizeChange = (val) => {
    setPageSize(val)
    localStorage.setItem('badgePageSize', val)
  }

  const handleClearRecents = async () => {
    setClearingLabels(true)
    const updated = await clearRecentBadgeLabels(
      badgeConfig?.id,
      activeSubjectType,
      recentLabelsByType
    )
    setRecentLabelsByType(updated)
    setClearingLabels(false)
  }

  const saveSubject = async (values) => {
    const payload = { ...values, counter: parseInt(values.counter) }
    if ((isEditing && badgeSubject.counter !== values.counter) || !isEditing) {
      const exists = await badgeCounterExists(
        payload.counter,
        conference?.id,
        selectedType || undefined
      )
      if (exists) {
        const lastBadge = await getHighestBadgeCounter(conference?.id, values.type || undefined)
        if (lastBadge) setNewBadgeCounter(lastBadge.counter)
        setIsBadgeCounterExists(true)
        return null
      }
    }
    let savedSubject = null
    if (isEditing) {
      savedSubject = await updateBadgeSubject(payload, subjectId, conference?.id, null)
    } else {
      savedSubject = await addBadgeSubject(payload, conference?.id, null)
    }
    return savedSubject ?? payload
  }

  const handleSubmit = async (values) => {
    setSaving(true)
    const intent = submitIntentRef.current
    const saved = await saveSubject(values)
    if (!saved) {
      setSaving(false)
      return
    }
    const printParams = { pageSize, orientation, bannerLabel }
    await updateRecentBadgeLabels(
      badgeConfig?.id,
      activeSubjectType,
      bannerLabel,
      recentLabelsByType
    ).then(setRecentLabelsByType)
    if (intent === 'print' && onSaveAndPrint) {
      await onSaveAndPrint([saved], printParams)
    } else if (intent === 'download' && onSaveAndDownload) {
      await onSaveAndDownload([saved], printParams)
    }
    setSaving(false)
    handleClose()
  }

  const handleClose = () => {
    setSelectedType('')
    setLastCounter(0)
    setIsBadgeCounterExists(false)
    setIsCheckingCounter(false)
    setBadgeConfig(null)
    setRecentLabelsByType({})
    setBannerLabel('')
    onClose()
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xxl" classes={{ paper: classes.dialogPaper }}>
      {/* Header */}
      <Box className={classes.dialogTitle}>
        <Typography variant="h5">
          {isEditing ? 'Edit Badge Subject' : 'Add Badge Subject'}
        </Typography>
        {!saving && (
          <IconButton onClick={handleClose} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <DialogContent className={classes.dialogContent} style={{ overflowY: 'auto', flex: 1 }}>
        {/* Fields — wait for subject data before mounting Formik in edit mode */}
        {!subjectReady ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={addBadgeSubjectSchema}
            onSubmit={handleSubmit}>
            {({ setFieldValue, values, validateForm, setTouched, errors, touched }) => {
              const previewSubject = {
                type: selectedType || badgeSubject?.type,
                firstName: values.firstName,
                lastName: values.lastName,
                credential: values.credential,
                position: values.position,
                organization: values.organization,
                email: values.email,
                phone: values.phone,
                city: values.city,
                state: values.state,
                country: values.country,
                counter: values.counter
              }
              const configForPreview = badgeConfig
                ? {
                    ...badgeConfig,
                    subjectLabels: { [previewSubject.type]: bannerLabel }
                  }
                : null

              return (
                <Form>
                  <Box className={classes.twoColLayout}>
                    {/* ── Left: type selector + form fields ── */}
                    <Box className={classes.formCol}>
                      {/* Type radio cards */}
                      <Box mb={3}>
                        <Typography className={classes.sectionLabel}>
                          Subject Type
                          {isEditing && (
                            <span className={classes.lockedNote}> (locked while editing)</span>
                          )}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap="8px" mt={1}>
                          {badgesSubjectsApplication.map((type) => {
                            const selected = selectedType === type
                            return (
                              <Box
                                key={type}
                                className={`${classes.radioCard} ${
                                  selected ? classes.radioCardSelected : ''
                                } ${isEditing ? classes.radioCardDisabled : ''}`}
                                onClick={() => {
                                  if (isEditing) return
                                  setSelectedType(type)
                                  setIsBadgeCounterExists(false)
                                }}>
                                <Radio
                                  checked={selected}
                                  color="primary"
                                  size="small"
                                  disabled={isEditing}
                                  style={{ padding: 0 }}
                                  onChange={() => {
                                    if (isEditing) return
                                    setSelectedType(type)
                                    setIsBadgeCounterExists(false)
                                  }}
                                />
                                <span className={classes.radioCardLabel}>{type}</span>
                              </Box>
                            )
                          })}
                        </Stack>
                        {touched.type && errors.type && (
                          <Typography
                            variant="caption"
                            color="error"
                            display="block"
                            style={{ marginTop: 6 }}>
                            {errors.type}
                          </Typography>
                        )}
                      </Box>

                      <Box className={classes.fieldsGrid}>
                        {/* Counter */}
                        <Box className={classes.counterBox}>
                          {(isCheckingCounter || loadingCounter) && (
                            <span className={classes.circularProgress}>
                              <CircularProgress size={18} />
                            </span>
                          )}
                          <CustomTextField
                            label="Counter"
                            name="counter"
                            type="number"
                            onChange={async (e) => {
                              const value = e.target.value
                              setFieldValue('counter', value)
                              await debouncedCheckCounter(value, badgeSubject?.counter)
                            }}
                          />
                        </Box>

                        <CustomTextField label="First Name" name="firstName" type="text" />
                        <CustomTextField label="Last Name" name="lastName" type="text" />
                        <CustomTextField label="Email" name="email" type="email" />
                        <CustomTextField label="Credential" name="credential" type="text" />
                        <CustomTextField label="Position/Title" name="position" type="text" />
                        <CustomTextField label="Organization" name="organization" type="text" />
                        <CustomTextField label="Phone" name="phone" type="text" />
                        <CustomTextField label="City" name="city" type="text" />
                        <CustomTextField label="State" name="state" type="text" />
                        <CustomTextField label="Country" name="country" type="text" />
                      </Box>

                      {/* Counter conflict alert */}
                      {isBadgeCounterExists && (
                        <Box mt={2}>
                          <Box className={classes.conflictBanner}>
                            <WarningIcon className={classes.conflictIcon} />
                            <Box flex={1}>
                              <Typography className={classes.conflictText}>
                                <strong>Counter {values.counter} already exists</strong> for type{' '}
                                <strong>{selectedType || badgeSubject?.type}</strong>.
                              </Typography>
                              <Typography className={classes.conflictQuestion}>
                                How would you like to handle this?
                              </Typography>
                              <Stack direction="row" spacing={1} mt={1}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  className={classes.conflictYesBtn}
                                  onClick={async () => {
                                    const errors = await validateForm()
                                    setTouched(
                                      Object.keys(values).reduce(
                                        (acc, k) => ({ ...acc, [k]: true }),
                                        {}
                                      )
                                    )
                                    if (Object.keys(errors).length > 0) return
                                    setIsBadgeCounterExists(false)
                                    setSaving(true)
                                    const payload = { ...values, counter: parseInt(values.counter) }
                                    if (isEditing) {
                                      await updateBadgeSubject(
                                        payload,
                                        subjectId,
                                        conference?.id,
                                        null
                                      )
                                    } else {
                                      await addBadgeSubject(payload, conference?.id, null)
                                    }
                                    setSaving(false)
                                    handleClose()
                                  }}>
                                  Yes — keep this counter
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  className={classes.conflictNoBtn}
                                  onClick={() => {
                                    if (lastCounter + 1 === parseInt(values.counter)) {
                                      setFieldValue('counter', newBadgeCounter + 1)
                                      setLastCounter(newBadgeCounter)
                                    } else {
                                      setFieldValue('counter', lastCounter + 1)
                                    }
                                    setIsBadgeCounterExists(false)
                                  }}>
                                  No — use next available
                                </Button>
                              </Stack>
                              <Box mt={1}>
                                <Typography className={classes.conflictHint}>
                                  <strong>Yes</strong> — saves with the same counter (duplicates
                                  allowed).
                                </Typography>
                                <Typography className={classes.conflictHint}>
                                  <strong>No</strong> — updates counter to the next available
                                  number.
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* ── Right: badge label + preview ── */}
                    <Box className={classes.previewCol}>
                      <Typography className={classes.previewColHeading}>Badge Preview</Typography>
                      {/* Badge label */}
                      <Box mb={1.5}>
                        <Typography variant="caption" className={classes.previewCaption}>
                          BADGE LABEL
                        </Typography>
                        <input
                          className={classes.labelInput}
                          value={bannerLabel}
                          onChange={(e) => setBannerLabel(e.target.value)}
                          placeholder="Enter badge label"
                        />
                        {bannerLabel.length > 50 ? (
                          <Typography variant="caption" color="error" display="block">
                            Label must not exceed 50 characters.
                          </Typography>
                        ) : (
                          <Typography className={classes.labelHelper}>
                            Applies only to this print job.
                          </Typography>
                        )}
                      </Box>

                      {/* Recent labels */}
                      <Box mb={1.5}>
                        <Box className={classes.recentsHeader}>
                          <Typography variant="caption" className={classes.previewCaption}>
                            RECENT LABELS
                          </Typography>
                          {recentLabels.length > 0 && (
                            <Button
                              size="small"
                              onClick={handleClearRecents}
                              disabled={clearingLabels}
                              className={classes.clearBtn}>
                              {clearingLabels ? <CircularProgress size={10} /> : 'Clear'}
                            </Button>
                          )}
                        </Box>
                        {recentLabels.length > 0 ? (
                          <Box className={classes.chipRow}>
                            {recentLabels.map((label) => (
                              <Chip
                                key={label}
                                label={label}
                                size="small"
                                variant={label === bannerLabel ? 'filled' : 'outlined'}
                                onClick={() => setBannerLabel(label)}
                                className={classes.recentChip}
                                sx={
                                  label === bannerLabel
                                    ? {
                                        backgroundColor: '#15b4db',
                                        color: '#fff',
                                        borderColor: '#15b4db'
                                      }
                                    : { borderColor: '#ccc', color: '#555' }
                                }
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography className={classes.noRecents}>
                            No recent labels yet.
                          </Typography>
                        )}
                      </Box>

                      <Divider style={{ marginBottom: '12px' }} />

                      {/* Orientation + Page size */}
                      <Box className={classes.printOptions}>
                        <Box>
                          <Typography variant="caption" className={classes.previewCaption}>
                            ORIENTATION
                          </Typography>
                          <Box className={classes.segmentedGroup} mt={0.5}>
                            {[
                              { value: 'landscape', label: 'Landscape', Icon: CropLandscape },
                              { value: 'portrait', label: 'Portrait', Icon: CropPortrait }
                            ].map(({ value, label, Icon }) => (
                              <Box
                                key={value}
                                className={`${classes.segmentedOption} ${
                                  orientation === value ? classes.segmentedOptionActive : ''
                                }`}
                                onClick={() => handleOrientationChange(value)}>
                                <Icon style={{ fontSize: 13, marginRight: 4 }} />
                                {label}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" className={classes.previewCaption}>
                            PAGE SIZE
                          </Typography>
                          <Box className={classes.segmentedGroup} mt={0.5}>
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                              <Box
                                key={opt.id}
                                className={`${classes.segmentedOption} ${
                                  pageSize === opt.id ? classes.segmentedOptionActive : ''
                                }`}
                                onClick={() => handlePageSizeChange(opt.id)}>
                                {opt.label}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>

                      {/* Preview */}
                      <Box className={classes.previewArea}>
                        {loadingConfig ? (
                          <Box className={classes.previewCenter}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : configForPreview ? (
                          <BadgePreview
                            badgeConfig={configForPreview}
                            orientation={orientation}
                            previewAs={previewSubject.type}
                            subject={previewSubject}
                          />
                        ) : (
                          <Box className={classes.previewCenter}>
                            <Typography className={classes.previewHint}>
                              {selectedType || badgeSubject?.type
                                ? `No configuration found for ${
                                    selectedType || badgeSubject?.type
                                  }.`
                                : 'Select a subject type to preview.'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Form>
              )
            }}
          </Formik>
        )}
      </DialogContent>

      {/* Footer — always visible, outside scrollable content */}
      <Box className={classes.dialogFooter}>
        <Divider />
        <Stack direction="row" spacing={1} className={classes.actions}>
          <Button size="large" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <ProgressButton
            type="submit"
            label="Save"
            inProgress={saving && submitIntentRef.current === 'save'}
            disabled={
              saving || isBadgeCounterExists || isCheckingCounter || bannerLabel.length > 50
            }
            onClick={() => {
              submitIntentRef.current = 'save'
              formikRef.current?.submitForm()
            }}
          />
          {onSaveAndPrint && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={
                saving && submitIntentRef.current === 'print' ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <Print />
                )
              }
              disabled={
                saving || isBadgeCounterExists || isCheckingCounter || bannerLabel.length > 50
              }
              onClick={() => {
                submitIntentRef.current = 'print'
                formikRef.current?.submitForm()
              }}>
              Save &amp; Print
            </Button>
          )}
          {onSaveAndDownload && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                saving && submitIntentRef.current === 'download' ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <Download />
                )
              }
              disabled={
                saving || isBadgeCounterExists || isCheckingCounter || bannerLabel.length > 50
              }
              onClick={() => {
                submitIntentRef.current = 'download'
                formikRef.current?.submitForm()
              }}>
              Save &amp; Download PDF
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  )
}

export default AddEditBadgeSubjectModal
